import { useState } from "react";

import { Icon_Arrow_Left, Icon_Arrow_Right } from "../../../styles/icons";
import styled from "styled-components";


import useApi from "../../../hooks/useApi";
import { useQuery } from "@tanstack/react-query";

import Loader from "../../generic/Loader";
import BarChartWidget from "./charts/BarChartWidget";
import IconButton from "../../generic/IconButton";

import addMissingMonths from "./utils/addMissingMonths";
import rankByGroup from "./utils/rankByGroup";
import useTranslation from "../../../hooks/useTranslation";
import useFormatting from "../../../hooks/useFormatting";
import { WidgetTitle, widgetFont } from "../widgetTypography";

const ChartContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`

const Title = styled.div`
    display: flex;
    justify-content: space-between;
    margin-left: 1rem;
    flex-shrink: 0;
`
const Navigator = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-right: 1rem;
`

const Period = styled.span`
    ${widgetFont.subtitle}
    margin-right: 0.5rem;
    white-space: nowrap;
`

const ChartArea = styled.div`
    flex: 1;
    min-height: 0;
    position: relative;
`

const ChartInner = styled.div`
    position: absolute;
    inset: 0;
`

const defaultData = [
    {
        name: 'Main WH',
        Quantity: 4000,
        Reserved: 2400,
    },
    {
        name: 'South WH',
        Quantity: 3000,
        Reserved: 1398,
    },
    {
        name: 'North WH',
        Quantity: 2000,
        Reserved: 9800,
    },
    {
        name: 'Small WH',
        Quantity: 2780,
        Reserved: 3908,
    }
];

const ChartWidget = ({ widgetData, type = '', labels = '' }) => {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth() + 1

    const numberOfMonths = 12; // Include 12 months in the result

    const [date, setDate] = useState({ year: year, month: month });
    const { getMe } = useApi()

    const { t } = useTranslation()
    const { formatString } = useFormatting()

    // The stat to read and the field each bar shows. A `groupBy` widget ranks
    // the stat's groups over the months in view instead of showing the months.
    const statsKey = widgetData?.stats
    const valueField = widgetData?.bars?.[0]?.dataKey
    const groupBy = widgetData?.groupBy

    const increaseMonth = () => {
        setDate((prevDate) => {
            const newMonth = prevDate.month + 1;
            return {
                year: newMonth > 12 ? prevDate.year + 1 : prevDate.year,
                month: newMonth > 12 ? 1 : newMonth,
            };
        });
    };

    const decreaseMonth = () => {
        setDate((prevDate) => {
            const newMonth = prevDate.month - 1;
            return {
                year: newMonth < 1 ? prevDate.year - 1 : prevDate.year,
                month: newMonth < 1 ? 12 : newMonth,
            };
        });
    };

    // Calculate the date range for the API call
    const getDateRange = () => {
        let toDate = new Date(date.year, date.month - 1);
        let fromDate = new Date(toDate.getFullYear(), toDate.getMonth() - numberOfMonths + 1);
        return {
            from: `${fromDate.getFullYear()}-${(fromDate.getMonth() + 1).toString().padStart(2, '0')}-01`,
            to: `${toDate.getFullYear()}-${(toDate.getMonth() + 1).toString().padStart(2, '0')}-31`
        };
    };

    const dateRange = getDateRange();

    // A grouped stat's keys start with the group, so the server cannot pick
    // months out of them: it is read whole, and rankByGroup keeps the months in view.
    const loadData = async () => {
        const range = groupBy ? '' : `&from=${dateRange.from}&to=${dateRange.to}`
        const response = await getMe(`stats/?stat=${statsKey}${range}`)
        return response
    }

    const { data: statsData, isLoading, isError, error } = useQuery({
        queryKey: ['stats', statsKey, groupBy ? 'all' : date],
        queryFn: loadData,
        enabled: widgetData?.load !== false,
        keepPreviousData: true
    })

    if (isLoading) return <Loader />

    if (isError) {
        console.error('Error fetching stats:', error)
        return <div>Error loading chart data: {error.message}</div>
    }

    let chartData
    if (widgetData?.load) {
        // A failed call resolves with the error body, not the stats array — only rows can be charted
        const rawData = Array.isArray(statsData?.data) ? statsData.data : []
        chartData = groupBy
            ? rankByGroup(rawData, { ...groupBy, field: valueField, endYear: date.year, endMonth: date.month, numberOfMonths, emptyName: t(groupBy.emptyName) })
            : addMissingMonths(rawData, valueField, date.year, date.month, numberOfMonths);
    } else {
        chartData = defaultData
    }
    return (
        <ChartContainer>
            <Title>
                <WidgetTitle>{t(labels.title)}</WidgetTitle>
                {widgetData.navigator && <Navigator>
                    {groupBy && <Period>
                        {formatString(new Date(date.year, date.month - numberOfMonths), 'monthYear')} – {formatString(new Date(date.year, date.month - 1), 'monthYear')}
                    </Period>}
                    <IconButton icon={Icon_Arrow_Left} $variant='primary' clickAction={decreaseMonth} />
                    <IconButton icon={Icon_Arrow_Right} $variant='primary' clickAction={increaseMonth} />
                </Navigator>}
            </Title>
            <ChartArea>
                <ChartInner>
                    <BarChartWidget widgetData={widgetData} chartData={chartData} />
                </ChartInner>
            </ChartArea>
        </ChartContainer>
    )
}

export default ChartWidget
